import { FunctionRegistry, flattenArgs, toNum, toStr } from './index';
import { FormulaValue, FormulaError, FormulaErrorType } from '../types';
import { smartRound } from '../evaluator';

// ---------------------------------------------------------------------------
// Internal financial helpers
// ---------------------------------------------------------------------------

/**
 * Excel PMT formula: computes periodic payment.
 * type=0: end of period, type=1: beginning of period.
 */
function calcPmt(rate: number, nper: number, pv: number, fv: number, type: number): number {
    if (rate === 0) {
        return -(pv + fv) / nper;
    }
    const pvif = Math.pow(1 + rate, nper);
    const pmt = (rate / (pvif - 1)) * (-(pv * pvif) - fv);
    return type === 1 ? pmt / (1 + rate) : pmt;
}

/**
 * Excel FV formula.
 */
function calcFv(rate: number, nper: number, pmt: number, pv: number, type: number): number {
    if (rate === 0) return -(pv + pmt * nper);
    const pvif = Math.pow(1 + rate, nper);
    return -(pv * pvif + pmt * (1 + rate * type) * (pvif - 1) / rate);
}

/**
 * Excel PV formula.
 */
function calcPv(rate: number, nper: number, pmt: number, fv: number, type: number): number {
    if (rate === 0) return -(pmt * nper + fv);
    const pvif = Math.pow(1 + rate, nper);
    return -(fv + pmt * (1 + rate * type) * (pvif - 1) / rate) / pvif;
}

/**
 * Excel IPMT: interest portion for a single period.
 */
function calcIpmt(rate: number, per: number, nper: number, pv: number, fv: number, type: number): number {
    const pmt = calcPmt(rate, nper, pv, fv, type);
    if (per === 1) {
        return type === 1 ? 0 : -pv * rate;
    }
    const fvPrev = calcFv(rate, per - 1, pmt, pv, type);
    return type === 1 ? fvPrev * rate / (1 + rate) : fvPrev * rate;
}

// ---------------------------------------------------------------------------

export function registerFinancialFunctions(registry: FunctionRegistry): void {
    // CUMIPMT(rate, nper, pv, startPeriod, endPeriod, type)
    registry.register('CUMIPMT', (...args: FormulaValue[]): FormulaValue => {
        const rate = toNum(args[0]);
        const nper = toNum(args[1]);
        const pv = toNum(args[2]);
        const startPeriod = Math.floor(toNum(args[3]));
        const endPeriod = Math.floor(toNum(args[4]));
        const type = Math.floor(toNum(args[5]));

        if (rate <= 0 || nper <= 0 || pv <= 0 || startPeriod < 1 || endPeriod < startPeriod || (type !== 0 && type !== 1)) {
            throw new FormulaError(FormulaErrorType.NUM, 'CUMIPMT: invalid arguments');
        }

        let total = 0;
        for (let per = startPeriod; per <= endPeriod; per++) {
            total += calcIpmt(rate, per, nper, pv, 0, type);
        }
        return smartRound(total);
    });

    // CUMPRINC(rate, nper, pv, startPeriod, endPeriod, type)
    registry.register('CUMPRINC', (...args: FormulaValue[]): FormulaValue => {
        const rate = toNum(args[0]);
        const nper = toNum(args[1]);
        const pv = toNum(args[2]);
        const startPeriod = Math.floor(toNum(args[3]));
        const endPeriod = Math.floor(toNum(args[4]));
        const type = Math.floor(toNum(args[5]));

        if (rate <= 0 || nper <= 0 || pv <= 0 || startPeriod < 1 || endPeriod < startPeriod || (type !== 0 && type !== 1)) {
            throw new FormulaError(FormulaErrorType.NUM, 'CUMPRINC: invalid arguments');
        }

        const pmt = calcPmt(rate, nper, pv, 0, type);
        let total = 0;
        for (let per = startPeriod; per <= endPeriod; per++) {
            total += pmt - calcIpmt(rate, per, nper, pv, 0, type);
        }
        return smartRound(total);
    });

    // DB(cost, salvage, life, period, month?): fixed-declining balance depreciation
    registry.register('DB', (...args: FormulaValue[]): FormulaValue => {
        const cost = toNum(args[0]);
        const salvage = toNum(args[1]);
        const life = toNum(args[2]);
        const period = toNum(args[3]);
        const month = args[4] !== undefined && args[4] !== null ? toNum(args[4]) : 12;

        if (cost <= 0 || salvage < 0 || life <= 0 || period <= 0) {
            throw new FormulaError(FormulaErrorType.NUM, 'DB: invalid arguments');
        }

        // Rate = 1 - (salvage/cost)^(1/life), rounded to 3 decimal places
        const rate = Math.round((1 - Math.pow(salvage / cost, 1 / life)) * 1000) / 1000;

        let remaining = cost;
        let dep = 0;

        for (let i = 1; i <= Math.floor(period); i++) {
            if (i === 1) {
                dep = cost * rate * month / 12;
            } else if (i === Math.floor(life) + 1) {
                dep = (remaining - remaining * rate * (12 - month) / 12);
            } else {
                dep = remaining * rate;
            }
            remaining -= dep;
        }
        return smartRound(dep);
    });

    // DDB(cost, salvage, life, period, factor?): double declining balance
    registry.register('DDB', (...args: FormulaValue[]): FormulaValue => {
        const cost = toNum(args[0]);
        const salvage = toNum(args[1]);
        const life = toNum(args[2]);
        const period = toNum(args[3]);
        const factor = args[4] !== undefined && args[4] !== null ? toNum(args[4]) : 2;

        if (cost < 0 || salvage < 0 || life <= 0 || period <= 0 || factor <= 0) {
            throw new FormulaError(FormulaErrorType.NUM, 'DDB: invalid arguments');
        }

        let bookValue = cost;
        let dep = 0;

        for (let i = 1; i <= Math.ceil(period); i++) {
            const fraction = Math.min(period, i) - Math.max(0, i - 1);
            const rate = Math.min(factor / life, 1);
            dep = Math.min(bookValue * rate, bookValue - salvage) * fraction;
            dep = Math.max(dep, 0);
            bookValue -= dep;
        }

        return smartRound(dep);
    });

    // DOLLARDE(fractionalDollar, fraction): convert to decimal dollar notation
    registry.register('DOLLARDE', (...args: FormulaValue[]): FormulaValue => {
        const fractionalDollar = toNum(args[0]);
        const fraction = Math.floor(toNum(args[1]));

        if (fraction < 1) {
            throw new FormulaError(FormulaErrorType.NUM, 'DOLLARDE: fraction must be >= 1');
        }

        const intPart = Math.trunc(fractionalDollar);
        const fracPart = (fractionalDollar - intPart);
        // The fractional part's digits represent numerator/fraction
        // e.g. 1.02 with fraction=8 → 1 + 2/8 = 1.25
        const digits = Math.pow(10, Math.ceil(Math.log10(fraction)));
        return smartRound(intPart + fracPart * digits / fraction);
    });

    // DOLLARFR(decimalDollar, fraction): convert decimal to fractional notation
    registry.register('DOLLARFR', (...args: FormulaValue[]): FormulaValue => {
        const decimalDollar = toNum(args[0]);
        const fraction = Math.floor(toNum(args[1]));

        if (fraction < 1) {
            throw new FormulaError(FormulaErrorType.NUM, 'DOLLARFR: fraction must be >= 1');
        }

        const intPart = Math.trunc(decimalDollar);
        const fracPart = decimalDollar - intPart;
        const digits = Math.pow(10, Math.ceil(Math.log10(fraction)));
        return smartRound(intPart + fracPart * fraction / digits);
    });

    // EFFECT(nominalRate, npery): effective annual interest rate
    registry.register('EFFECT', (...args: FormulaValue[]): FormulaValue => {
        const nominalRate = toNum(args[0]);
        const npery = Math.floor(toNum(args[1]));

        if (nominalRate <= 0 || npery < 1) {
            throw new FormulaError(FormulaErrorType.NUM, 'EFFECT: invalid arguments');
        }

        return smartRound(Math.pow(1 + nominalRate / npery, npery) - 1);
    });

    // FV(rate, nper, pmt, pv?, type?): future value
    registry.register('FV', (...args: FormulaValue[]): FormulaValue => {
        const rate = toNum(args[0]);
        const nper = toNum(args[1]);
        const pmt = toNum(args[2]);
        const pv = args[3] !== undefined && args[3] !== null ? toNum(args[3]) : 0;
        const type = args[4] !== undefined && args[4] !== null ? toNum(args[4]) : 0;

        return smartRound(calcFv(rate, nper, pmt, pv, type));
    });

    // FVSCHEDULE(principal, schedule): FV with variable rates
    registry.register('FVSCHEDULE', (...args: FormulaValue[]): FormulaValue => {
        const principal = toNum(args[0]);
        const schedule = flattenArgs(args.slice(1));

        let fv = principal;
        for (const rate of schedule) {
            fv *= (1 + rate);
        }
        return smartRound(fv);
    });

    // IPMT(rate, per, nper, pv, fv?, type?): interest payment
    registry.register('IPMT', (...args: FormulaValue[]): FormulaValue => {
        const rate = toNum(args[0]);
        const per = toNum(args[1]);
        const nper = toNum(args[2]);
        const pv = toNum(args[3]);
        const fv = args[4] !== undefined && args[4] !== null ? toNum(args[4]) : 0;
        const type = args[5] !== undefined && args[5] !== null ? toNum(args[5]) : 0;

        return smartRound(calcIpmt(rate, per, nper, pv, fv, type));
    });

    // IRR(values, guess?): internal rate of return via Newton-Raphson
    registry.register('IRR', (...args: FormulaValue[]): FormulaValue => {
        const values = flattenArgs([args[0]]);
        const guess = args[1] !== undefined && args[1] !== null ? toNum(args[1]) : 0.1;

        if (values.length < 2) {
            throw new FormulaError(FormulaErrorType.VALUE, 'IRR: requires at least 2 values');
        }

        const npv = (rate: number): number => {
            return values.reduce((sum, val, i) => sum + val / Math.pow(1 + rate, i), 0);
        };
        const npvDeriv = (rate: number): number => {
            return values.reduce((sum, val, i) => sum - i * val / Math.pow(1 + rate, i + 1), 0);
        };

        let rate = guess;
        for (let iter = 0; iter < 100; iter++) {
            const f = npv(rate);
            const df = npvDeriv(rate);

            if (Math.abs(df) < 1e-20) break;

            const newRate = rate - f / df;
            if (Math.abs(newRate - rate) < 1e-10) {
                return smartRound(newRate);
            }
            rate = newRate;
        }

        throw new FormulaError(FormulaErrorType.NUM, 'IRR: did not converge');
    });

    // ISPMT(rate, per, nper, pv): simple interest payment for a period
    registry.register('ISPMT', (...args: FormulaValue[]): FormulaValue => {
        const rate = toNum(args[0]);
        const per = toNum(args[1]);
        const nper = toNum(args[2]);
        const pv = toNum(args[3]);

        // Excel formula: =pv * rate * (per/nper - 1)
        return smartRound(pv * rate * (per / nper - 1));
    });

    // MIRR(values, financeRate, reinvestRate): modified IRR
    registry.register('MIRR', (...args: FormulaValue[]): FormulaValue => {
        const values = flattenArgs([args[0]]);
        const financeRate = toNum(args[1]);
        const reinvestRate = toNum(args[2]);
        const n = values.length;

        if (n < 2) {
            throw new FormulaError(FormulaErrorType.VALUE, 'MIRR: requires at least 2 values');
        }

        // NPV of negative cash flows at finance rate
        const negativePV = values.reduce((sum, v, i) => {
            return v < 0 ? sum + v / Math.pow(1 + financeRate, i) : sum;
        }, 0);

        // FV of positive cash flows at reinvest rate
        const positiveFV = values.reduce((sum, v, i) => {
            return v > 0 ? sum + v * Math.pow(1 + reinvestRate, n - 1 - i) : sum;
        }, 0);

        if (negativePV === 0 || positiveFV === 0) {
            throw new FormulaError(FormulaErrorType.DIV_BY_ZERO, 'MIRR: division by zero');
        }

        return smartRound(Math.pow(-positiveFV / negativePV, 1 / (n - 1)) - 1);
    });

    // NOMINAL(effectRate, npery): nominal annual interest rate
    registry.register('NOMINAL', (...args: FormulaValue[]): FormulaValue => {
        const effectRate = toNum(args[0]);
        const npery = Math.floor(toNum(args[1]));

        if (effectRate <= 0 || npery < 1) {
            throw new FormulaError(FormulaErrorType.NUM, 'NOMINAL: invalid arguments');
        }

        return smartRound(npery * (Math.pow(1 + effectRate, 1 / npery) - 1));
    });

    // NPER(rate, pmt, pv, fv?, type?): number of periods
    registry.register('NPER', (...args: FormulaValue[]): FormulaValue => {
        const rate = toNum(args[0]);
        const pmt = toNum(args[1]);
        const pv = toNum(args[2]);
        const fv = args[3] !== undefined && args[3] !== null ? toNum(args[3]) : 0;
        const type = args[4] !== undefined && args[4] !== null ? toNum(args[4]) : 0;

        if (rate === 0) {
            if (pmt === 0) {
                throw new FormulaError(FormulaErrorType.DIV_BY_ZERO, 'NPER: pmt cannot be 0 when rate is 0');
            }
            return smartRound(-(pv + fv) / pmt);
        }

        const num = fv - pmt * (1 + rate * type) / rate;
        const den = pv + pmt * (1 + rate * type) / rate;

        if (den === 0 || num / den <= 0) {
            throw new FormulaError(FormulaErrorType.NUM, 'NPER: no valid solution');
        }

        return smartRound(Math.log(num / den) / Math.log(1 + rate));
    });

    // NPV(rate, ...values): net present value
    registry.register('NPV', (...args: FormulaValue[]): FormulaValue => {
        const rate = toNum(args[0]);
        const values = flattenArgs(args.slice(1));

        const npv = values.reduce((sum, val, i) => {
            return sum + val / Math.pow(1 + rate, i + 1);
        }, 0);

        return smartRound(npv);
    });

    // PDURATION(rate, pv, fv): number of periods for investment to reach fv
    registry.register('PDURATION', (...args: FormulaValue[]): FormulaValue => {
        const rate = toNum(args[0]);
        const pv = toNum(args[1]);
        const fv = toNum(args[2]);

        if (rate <= 0 || pv <= 0 || fv <= 0) {
            throw new FormulaError(FormulaErrorType.NUM, 'PDURATION: rate, pv, and fv must be positive');
        }

        return smartRound((Math.log(fv) - Math.log(pv)) / Math.log(1 + rate));
    });

    // PMT(rate, nper, pv, fv?, type?): periodic payment
    registry.register('PMT', (...args: FormulaValue[]): FormulaValue => {
        const rate = toNum(args[0]);
        const nper = toNum(args[1]);
        const pv = toNum(args[2]);
        const fv = args[3] !== undefined && args[3] !== null ? toNum(args[3]) : 0;
        const type = args[4] !== undefined && args[4] !== null ? toNum(args[4]) : 0;

        return smartRound(calcPmt(rate, nper, pv, fv, type));
    });

    // PPMT(rate, per, nper, pv, fv?, type?): principal payment
    registry.register('PPMT', (...args: FormulaValue[]): FormulaValue => {
        const rate = toNum(args[0]);
        const per = toNum(args[1]);
        const nper = toNum(args[2]);
        const pv = toNum(args[3]);
        const fv = args[4] !== undefined && args[4] !== null ? toNum(args[4]) : 0;
        const type = args[5] !== undefined && args[5] !== null ? toNum(args[5]) : 0;

        const pmt = calcPmt(rate, nper, pv, fv, type);
        const ipmt = calcIpmt(rate, per, nper, pv, fv, type);
        return smartRound(pmt - ipmt);
    });

    // PV(rate, nper, pmt, fv?, type?): present value
    registry.register('PV', (...args: FormulaValue[]): FormulaValue => {
        const rate = toNum(args[0]);
        const nper = toNum(args[1]);
        const pmt = toNum(args[2]);
        const fv = args[3] !== undefined && args[3] !== null ? toNum(args[3]) : 0;
        const type = args[4] !== undefined && args[4] !== null ? toNum(args[4]) : 0;

        return smartRound(calcPv(rate, nper, pmt, fv, type));
    });

    // RATE(nper, pmt, pv, fv?, type?, guess?): interest rate per period
    registry.register('RATE', (...args: FormulaValue[]): FormulaValue => {
        const nper = toNum(args[0]);
        const pmt = toNum(args[1]);
        const pv = toNum(args[2]);
        const fv = args[3] !== undefined && args[3] !== null ? toNum(args[3]) : 0;
        const type = args[4] !== undefined && args[4] !== null ? toNum(args[4]) : 0;
        const guess = args[5] !== undefined && args[5] !== null ? toNum(args[5]) : 0.1;

        // f(rate) = PV*(1+rate)^nper + PMT*(1+rate*type)*((1+rate)^nper - 1)/rate + FV = 0
        const f = (rate: number): number => {
            if (Math.abs(rate) < 1e-10) {
                return pv + pmt * nper + fv;
            }
            const pvif = Math.pow(1 + rate, nper);
            return pv * pvif + pmt * (1 + rate * type) * (pvif - 1) / rate + fv;
        };

        const df = (rate: number): number => {
            if (Math.abs(rate) < 1e-10) {
                return pv * nper;
            }
            const pvif = Math.pow(1 + rate, nper);
            const term1 = pv * nper * pvif / (1 + rate);
            const term2 = pmt * (1 + rate * type) * (nper * pvif / (1 + rate) * rate - (pvif - 1)) / (rate * rate);
            return term1 + term2;
        };

        let rate = guess;
        for (let iter = 0; iter < 100; iter++) {
            const fVal = f(rate);
            const dfVal = df(rate);

            if (Math.abs(dfVal) < 1e-20) break;

            const newRate = rate - fVal / dfVal;
            if (Math.abs(newRate - rate) < 1e-10) {
                return smartRound(newRate);
            }
            rate = newRate;
        }

        throw new FormulaError(FormulaErrorType.NUM, 'RATE: did not converge');
    });

    // RRI(nper, pv, fv): equivalent interest rate for investment growth
    registry.register('RRI', (...args: FormulaValue[]): FormulaValue => {
        const nper = toNum(args[0]);
        const pv = toNum(args[1]);
        const fv = toNum(args[2]);

        if (nper <= 0 || pv <= 0 || fv <= 0) {
            throw new FormulaError(FormulaErrorType.NUM, 'RRI: nper, pv, and fv must be positive');
        }

        return smartRound(Math.pow(fv / pv, 1 / nper) - 1);
    });

    // SLN(cost, salvage, life): straight-line depreciation
    registry.register('SLN', (...args: FormulaValue[]): FormulaValue => {
        const cost = toNum(args[0]);
        const salvage = toNum(args[1]);
        const life = toNum(args[2]);

        if (life === 0) {
            throw new FormulaError(FormulaErrorType.DIV_BY_ZERO, 'SLN: life cannot be 0');
        }

        return smartRound((cost - salvage) / life);
    });

    // SYD(cost, salvage, life, per): sum-of-years-digits depreciation
    registry.register('SYD', (...args: FormulaValue[]): FormulaValue => {
        const cost = toNum(args[0]);
        const salvage = toNum(args[1]);
        const life = toNum(args[2]);
        const per = toNum(args[3]);

        if (life <= 0 || per <= 0 || per > life) {
            throw new FormulaError(FormulaErrorType.NUM, 'SYD: invalid arguments');
        }

        const sumOfYears = life * (life + 1) / 2;
        return smartRound((cost - salvage) * (life - per + 1) / sumOfYears);
    });

    // TBILLEQ(settlement, maturity, discount): T-bill equivalent yield
    registry.register('TBILLEQ', (...args: FormulaValue[]): FormulaValue => {
        const settlement = Math.floor(toNum(args[0]));
        const maturity = Math.floor(toNum(args[1]));
        const discount = toNum(args[2]);

        const dsm = maturity - settlement; // days to maturity

        if (dsm <= 0 || dsm > 365 || discount <= 0) {
            throw new FormulaError(FormulaErrorType.NUM, 'TBILLEQ: invalid arguments');
        }

        // Price per $100 face value
        const price = 100 * (1 - discount * dsm / 360);
        // Bond-equivalent yield
        return smartRound(365 * discount / (360 - discount * dsm));
    });

    // TBILLPRICE(settlement, maturity, discount): T-bill price per $100
    registry.register('TBILLPRICE', (...args: FormulaValue[]): FormulaValue => {
        const settlement = Math.floor(toNum(args[0]));
        const maturity = Math.floor(toNum(args[1]));
        const discount = toNum(args[2]);

        const dsm = maturity - settlement;

        if (dsm <= 0 || dsm > 365 || discount <= 0 || discount >= 1) {
            throw new FormulaError(FormulaErrorType.NUM, 'TBILLPRICE: invalid arguments');
        }

        return smartRound(100 * (1 - discount * dsm / 360));
    });

    // TBILLYIELD(settlement, maturity, price): T-bill yield
    registry.register('TBILLYIELD', (...args: FormulaValue[]): FormulaValue => {
        const settlement = Math.floor(toNum(args[0]));
        const maturity = Math.floor(toNum(args[1]));
        const price = toNum(args[2]);

        const dsm = maturity - settlement;

        if (dsm <= 0 || dsm > 365 || price <= 0) {
            throw new FormulaError(FormulaErrorType.NUM, 'TBILLYIELD: invalid arguments');
        }

        return smartRound((100 - price) / price * (360 / dsm));
    });

    // XNPV(rate, values, dates): NPV with irregular cash flow dates
    registry.register('XNPV', (...args: FormulaValue[]): FormulaValue => {
        const rate = toNum(args[0]);
        const values = flattenArgs([args[1]]);
        const dates = flattenArgs([args[2]]);

        if (values.length !== dates.length) {
            throw new FormulaError(FormulaErrorType.VALUE, 'XNPV: values and dates must have the same length');
        }
        if (values.length === 0) {
            throw new FormulaError(FormulaErrorType.VALUE, 'XNPV: requires at least one value');
        }

        const firstDate = dates[0];
        const xnpv = values.reduce((sum, val, i) => {
            const yearFrac = (dates[i] - firstDate) / 365;
            return sum + val / Math.pow(1 + rate, yearFrac);
        }, 0);

        return smartRound(xnpv);
    });

    // Keep linter satisfied for re-exported helpers used by other modules
    void toStr;
}
